import React from 'react';
import {ScrollView, TouchableOpacity, View} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import SlideModal from '@/Atoms/SlideModal';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

export type ChessModalAccent = 'gold' | 'teal' | 'red';

// Badge tint per accent; the gold top line and the faint gold board
// coordinates are part of the shell's signature and stay gold regardless
// of accent (matches the original delete-workbook modal, which paired a
// red badge with the gold line).
const ACCENTS: Record<
  ChessModalAccent,
  {badgeBg: string; badgeBorder: string; icon: string}
> = {
  gold: {
    badgeBg: 'rgba(250,207,71,0.15)',
    badgeBorder: 'rgba(250,207,71,0.3)',
    icon: '#FACF47',
  },
  teal: {
    badgeBg: 'rgba(61,171,158,0.15)',
    badgeBorder: 'rgba(61,171,158,0.35)',
    icon: '#5ECFC1',
  },
  red: {
    badgeBg: 'rgba(239,68,68,0.15)',
    badgeBorder: 'rgba(239,68,68,0.3)',
    icon: '#FF6B6B',
  },
};

// The 7px alternating dark/cream strip that divides the dark header from
// the cream body - the shell's signature element. Exported for surfaces
// (flyouts, panels) that want the brand mark without the whole shell.
export function CheckeredStrip({height = 7}: {height?: number}) {
  return (
    <View style={{flexDirection: 'row', height}} pointerEvents="none">
      {Array.from({length: 20}).map((_, i) => (
        <View
          key={i}
          style={{
            flex: 1,
            height,
            backgroundColor: i % 2 === 0 ? '#2A1F1A' : '#FAF6EB',
          }}
        />
      ))}
    </View>
  );
}

type ChessModalShellProps = {
  visible: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  // MaterialCommunityIcons name shown in the badge chip and as the large
  // ghost watermark behind the header.
  piece?: string;
  pieceRotation?: number;
  // Override the header watermark separately from the badge (e.g. queen
  // watermark behind a toppled-king badge).
  ghostPiece?: string;
  accent?: ChessModalAccent;
  // Compact drops the badge chip and tightens the header - for small
  // confirms where the full 52px badge treatment is too heavy.
  compact?: boolean;
  showClose?: boolean;
  maxWidth?: number;
  // Caps the body and makes it scroll - pass a CSS length ('60vh').
  // Defaults to '55vh' so no popup ever stretches the full viewport
  // height; long content scrolls inside the cream body instead.
  bodyMaxHeight?: string;
  useNativeModal?: boolean;
  children: React.ReactNode;
  // Rendered under the body content, above the bottom padding - typically
  // a row of PillButtons.
  footer?: React.ReactNode;
};

// Reusable chess-branded modal shell: dark walnut header with checkerboard
// watermark, ghost piece, gold accent line and board coordinates; the
// checkered edge strip; cream scrollable body. Extracted from the workbook
// list's delete-confirm modal so every Workbook popup reads as one set.
function ChessModalShell({
  visible,
  onClose,
  title,
  subtitle,
  piece = 'chess-pawn',
  pieceRotation = 0,
  ghostPiece,
  accent = 'gold',
  compact = false,
  showClose = true,
  maxWidth = 400,
  bodyMaxHeight = '55vh',
  useNativeModal,
  children,
  footer,
}: ChessModalShellProps) {
  const tint = ACCENTS[accent];

  const body = (
    <View
      style={{
        paddingHorizontal: 24,
        paddingTop: 20,
        paddingBottom: 24,
        gap: 20,
      }}>
      {children}
      {footer && (
        <View style={{flexDirection: 'row', gap: 10, justifyContent: 'center'}}>
          {footer}
        </View>
      )}
    </View>
  );

  return (
    <SlideModal
      visible={visible}
      onClose={onClose}
      type="center"
      showHeader={false}
      useNativeModal={useNativeModal}
      customStyle={{
        borderRadius: 20,
        overflow: 'hidden',
        padding: 0,
        backgroundColor: '#FAF6EB',
        borderWidth: 1,
        borderColor: 'rgba(77,63,55,0.12)',
        // A fixed-width centered box: exactly `maxWidth` wide on desktop,
        // never stretching with the viewport; the percentage cap only
        // kicks in on screens narrower than the box.
        width: maxWidth,
        maxWidth: '92%',
        minWidth: 0,
        alignSelf: 'center',
      }}>
      {/* ── Dark chess header strip ─────────────────────────────────── */}
      <View
        style={{
          backgroundColor: '#2A1F1A',
          paddingHorizontal: 24,
          paddingTop: compact ? 18 : 24,
          paddingBottom: compact ? 16 : 22,
          position: 'relative',
          overflow: 'hidden',
        }}>
        {/* Checkerboard watermark */}
        <View
          style={{
            position: 'absolute',
            right: -8,
            bottom: -8,
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: 80,
            height: 80,
            opacity: 0.06,
          }}
          pointerEvents="none">
          {Array.from({length: 64}).map((_, i) => {
            const r = Math.floor(i / 8);
            const c = i % 8;
            return (
              <View
                key={i}
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor:
                    (r + c) % 2 === 0 ? '#FFFFFF' : 'transparent',
                }}
              />
            );
          })}
        </View>

        {/* Ghost piece watermark */}
        <View
          style={{position: 'absolute', right: 16, top: -10, opacity: 0.05}}
          pointerEvents="none">
          <MaterialCommunityIcons
            name={ghostPiece || piece}
            size={compact ? 76 : 100}
            color="#FFFFFF"
          />
        </View>

        {/* Gold top accent line */}
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 24,
            right: 24,
            height: 2,
            backgroundColor: '#FACF47',
            opacity: 0.6,
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
          }}
          pointerEvents="none"
        />

        {/* Board coordinate labels - corner flavor like a chessboard edge */}
        <CCText
          fontSize={10}
          color="rgba(250,207,71,0.35)"
          style={{
            position: 'absolute',
            top: 10,
            left: 24,
            fontFamily: 'monospace',
            letterSpacing: 1,
          }}
          pointerEvents="none">
          h8
        </CCText>
        <CCText
          fontSize={10}
          color="rgba(250,207,71,0.35)"
          style={{
            position: 'absolute',
            bottom: 8,
            right: 24,
            fontFamily: 'monospace',
            letterSpacing: 1,
          }}
          pointerEvents="none">
          a1
        </CCText>

        {showClose && (
          <TouchableOpacity
            onPress={onClose}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}>
            <Ionicons
              name="close-outline"
              size={18}
              color="rgba(255,255,255,0.7)"
            />
          </TouchableOpacity>
        )}

        {compact ? (
          // Compact: small badge and title side by side
          <Box style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                backgroundColor: tint.badgeBg,
                borderWidth: 1,
                borderColor: tint.badgeBorder,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons
                name={piece}
                size={20}
                color={tint.icon}
                style={
                  pieceRotation
                    ? {transform: [{rotate: `${pieceRotation}deg`}]}
                    : undefined
                }
              />
            </View>
            <Box style={{flex: 1}}>
              <CCText
                fontSize={16}
                fontWeight="bold"
                color="#FFFFFF"
                style={{letterSpacing: -0.3}}>
                {title}
              </CCText>
              {!!subtitle && (
                <CCText
                  fontSize={11.5}
                  color="rgba(255,255,255,0.45)"
                  style={{lineHeight: 16, marginTop: 2}}>
                  {subtitle}
                </CCText>
              )}
            </Box>
          </Box>
        ) : (
          <>
            <View
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                backgroundColor: tint.badgeBg,
                borderWidth: 1,
                borderColor: tint.badgeBorder,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 14,
              }}>
              <MaterialCommunityIcons
                name={piece}
                size={26}
                color={tint.icon}
                style={
                  pieceRotation
                    ? {
                        transform: [{rotate: `${pieceRotation}deg`}],
                        marginTop: -2,
                      }
                    : undefined
                }
              />
            </View>
            <CCText
              fontSize={18}
              fontWeight="bold"
              color="#FFFFFF"
              style={{letterSpacing: -0.4, marginBottom: 6}}>
              {title}
            </CCText>
            {!!subtitle && (
              <CCText
                fontSize={12}
                color="rgba(255,255,255,0.45)"
                style={{lineHeight: 18}}>
                {subtitle}
              </CCText>
            )}
          </>
        )}
      </View>

      {/* ── Checkered edge - the board's border strip ────────────────── */}
      <CheckeredStrip />

      {/* ── Body ─────────────────────────────────────────────────────── */}
      {bodyMaxHeight ? (
        <ScrollView
          style={{maxHeight: bodyMaxHeight} as any}
          showsVerticalScrollIndicator={false}>
          {body}
        </ScrollView>
      ) : (
        body
      )}
    </SlideModal>
  );
}

export default ChessModalShell;
